import { Component, OnInit, ViewChild, ElementRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { InternalChatbotService } from '../../../core/services/internal-chatbot.service';
import { InternalDocument } from '../../../core/models/internal-chatbot.model';
import { AppUrlConstants } from '../../../core/constants/AppUrlConstants';

@Component({
  selector: 'app-upload-documents',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './upload-documents.component.html',
  styleUrl: './upload-documents.component.css'
})
export class UploadDocumentsComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef;

  // Data properties
  data: InternalDocument[] = [];
  filtered: InternalDocument[] = [];
  pageData: InternalDocument[] = [];
  loading = false;
  
  // Pagination properties
  page = 1;
  rowsPerPage = 10;
  totalPages = 1;
  skeletonRows = Array(10);

  // Search property
  search = '';

  // Modal properties
  showAddModal = false;
  showEditModal = false;
  showDeleteModal = false;
  showPreviewModal = false;

  // Form properties — multiple files = one PROCESSING task per file
  selectedFiles: File[] = [];
  pdfPreviewUrl: SafeResourceUrl | null = null;
  deleteRowId: string | null = null;
  editRowId: string | null = null;
  deleteRow: InternalDocument | null = null;

  // Toast notifications
  toasts: { message: string; type: 'success' | 'error' }[] = [];

  // Upload progress
  uploadProgress = 0;
  isUploading = false;

  constructor(private internalChatbotService: InternalChatbotService, private sanitizer: DomSanitizer) {}

  ngOnInit() {
    this.loadDocuments();
  }

  /**
   * Load documents from backend
   */
  loadDocuments() {
    this.loading = true;
    this.internalChatbotService.getDocuments().subscribe({
      next: (response) => {
        this.data = response;
        this.applyFilters();
        this.loading = false;
      },
      error: () => {
        this.showToast('Failed to load documents', 'error');
        this.loading = false;
      }
    });
  }

  /**
   * Apply search filter
   */
  onSearchChange() {
    this.page = 1;
    this.applyFilters();
  }

  /**
   * Apply filters and pagination
   */
  private applyFilters() {
    this.filtered = this.data.filter(doc =>
      doc.documentName.toLowerCase().includes(this.search.toLowerCase())
    );

    this.totalPages = Math.ceil(this.filtered.length / this.rowsPerPage);
    this.updatePageData();
  }

  /**
   * Update page data based on current page and rows per page
   */
  private updatePageData() {
    const start = (this.page - 1) * this.rowsPerPage;
    const end = start + this.rowsPerPage;
    this.pageData = this.filtered.slice(start, end);
  }

  /**
   * Handle page change
   */
  onPageChange() {
    this.updatePageData();
  }

  /**
   * Get row index
   */
  getRowIndex(idx: number): number {
    return (this.page - 1) * this.rowsPerPage + idx + 1;
  }

  /**
   * Get row class
   */
  getRowClass(idx: number): string {
    return idx % 2 === 0 ? 'qu-tr-even' : 'qu-tr-odd';
  }

  /**
   * Get row ID
   */
  getRowId(row: InternalDocument): string {
    return row.documentId.toString();
  }

  getFileType(documentName: string): string {
    const ext = documentName?.split('.').pop()?.toUpperCase();
    return ext || '—';
  }

  formatUploadDate(dateStr: string): string {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleDateString('en-PK', { dateStyle: 'medium' });
  }

  formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
  }

  /**
   * Handle file selection
   */
  onFileSelected(event: Event) {
    const input = event.target as HTMLInputElement;
    const files = input.files;

    if (!files || files.length === 0) {
      return;
    }

    const picked = Array.from(files);
    const invalid = picked.find(f => f.type !== 'application/pdf');
    if (invalid) {
      this.showToast(`Not a PDF file: ${invalid.name}`, 'error');
      return;
    }

    this.selectedFiles = picked;
    this.pdfPreviewUrl = null;
    if (picked.length === 1) {
      this.generatePdfPreview(picked[0]);
    }
  }

  /**
   * Generate PDF preview
   */
  private generatePdfPreview(file: File) {
    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result;
      if (typeof result === 'string') {
        this.pdfPreviewUrl = this.sanitizer.bypassSecurityTrustResourceUrl(result);
      }
    };
    reader.readAsDataURL(file);
  }

  /**
   * Open add modal
   */
  openAddModal() {
    this.showAddModal = true;
    this.selectedFiles = [];
    this.pdfPreviewUrl = null;
    this.uploadProgress = 0;
  }

  /**
   * Close add modal
   */
  closeAddModal() {
    this.showAddModal = false;
    this.selectedFiles = [];
    this.pdfPreviewUrl = null;
    this.uploadProgress = 0;
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  /**
   * Handle file upload
   */
  handleAdd() {
    if (this.selectedFiles.length === 0) {
      this.showToast('Please select a file', 'error');
      return;
    }

    this.uploadFiles();
  }

  /**
   * Upload files — the backend stores the bytes and returns immediately;
   * chunking/embedding runs async (tracked on Documents History).
   */
  private uploadFiles() {
    if (this.selectedFiles.length === 0) return;

    this.isUploading = true;
    this.internalChatbotService.uploadDocuments(this.selectedFiles, pct => {
      this.uploadProgress = pct;
    }).subscribe({
      next: () => {
        this.isUploading = false;
        this.showToast(
          'Document(s) uploaded. Processing started — track progress in Documents History.',
          'success'
        );
        this.closeAddModal();
        this.loadDocuments();
      },
      error: (err) => {
        const msg = err?.error?.message || 'Failed to upload document';
        this.showToast(msg, 'error');
        this.isUploading = false;
      }
    });
  }

  /**
   * Status badge class for the document status column
   */
  getStatusClass(status: string): string {
    const s = (status || '').toUpperCase();
    if (s === 'COMPLETED') return 'st-completed';
    if (s === 'PROCESSING') return 'st-progress';
    if (s === 'FAILED') return 'st-failed';
    return '';
  }



  /**
   * Confirm delete
   */
  confirmDeleteRow(rowId: string) {
    this.deleteRowId = rowId;
    this.showDeleteModal = true;
  }

  /**
   * Handle delete
   */
  handleDelete() {
    if (!this.deleteRowId) return;

    this.internalChatbotService.deleteDocument(this.deleteRowId).subscribe({
      next: () => {
        this.showToast('Document deleted successfully', 'success');
        this.showDeleteModal = false;
        this.loadDocuments();
      },
      error: () => {
        this.showToast('Failed to delete document', 'error');
      }
    });
  }

  /**
   * Show toast notification
   */
  private showToast(message: string, type: 'success' | 'error') {
    this.toasts.push({ message, type });
    setTimeout(() => {
      this.toasts.shift();
    }, 3000);
  }

  /**
   * Open PDF preview modal
   */
  openPreviewModal(row: InternalDocument) {
    this.editRowId = row.documentId.toString();
    this.showPreviewModal = true;
  }

  /**
   * Close preview modal
   */
  closePreviewModal() {
    this.showPreviewModal = false;
    this.editRowId = null;
  }

  /**
   * Download file
   */
  downloadFile(row: InternalDocument) {
    const link = document.createElement('a');
    link.href = `${AppUrlConstants.INTERNAL_DOCUMENTS_URL}/${row.documentId}/download`;
    link.download = row.documentName;
    link.click();
  }

  /**
   * Trigger file input
   */
  triggerFileInput() {
    this.fileInput.nativeElement.click();
  }
}
